import { IAccountModel } from './models/accountModel';
import { IOrganizationModel } from './models/organizationModel';
import { ILocationModel } from './models/locationModel';
import { IProviderModel } from './models/providerModel';
import { IUserModel } from './models/userModel';
import { Buffer } from 'buffer';
import { IEligibilityModel } from './models/eligibilityModel';
import config from '../config';

export const apiVersion = 'v1';
export const getApiBaseUrl = (lang: string) => {
    return (
        lang === 'fr'
            ? config.BACKEND_URL_FR
            : config.BACKEND_URL_EN
    ).concat('/', apiVersion, '/', lang);
};

export const ACCOUNT_ID_PARAM = 'accountId';
export const REGISTRATION_ID_PARAM = 'registrationId';
export const REFERENCE_NO_PARAM = 'referenceNo';

export default function useNetwork() {
    const defaultHeader = (noBody?: boolean) => {
        const urlParams = new URLSearchParams(window.location.search);
        const accountId = urlParams.get(ACCOUNT_ID_PARAM);
        const registrationId = urlParams.get(REGISTRATION_ID_PARAM);

        const header = {
            accept: 'application/json',
            authorization:
                'Basic ' +
                (accountId && registrationId
                    ? Buffer.from(accountId?.concat(':', registrationId!)).toString('base64')
                    : ''),
        };
        if (!noBody) {
            return {
                'Content-Type': 'application/json',
                ...header,
            };
        }
        return header;
    };

    const fetchInsurer = async (lang: string) => {
        return fetch(getApiBaseUrl(lang).concat('/insurers'), {
            method: 'GET',
            headers: defaultHeader(),
        }).then((data: Response) => {
            return data;
        });
    };

    const createAccount = async (lang: string, details: IAccountModel) => {
        return fetch(getApiBaseUrl(lang).concat('/accounts'), {
            method: 'POST',
            headers: defaultHeader(),
            body: JSON.stringify(details),
        }).then((data: Response) => {
            return data;
        });
    };

    const sendEmail = async (lang: string, email: string) => {
        return fetch(getApiBaseUrl(lang).concat('/accounts/', 'email/', email), {
            method: 'POST',
            headers: defaultHeader(true),
        }).then((data: Response) => {
            return data;
        });
    };

    const updateAccount = async (lang: string, id: string, details: IAccountModel) => {
        return fetch(getApiBaseUrl(lang).concat('/accounts/', id.toString()), {
            method: 'PUT',
            headers: defaultHeader(),
            body: JSON.stringify(details),
        }).then((data: Response) => {
            return data;
        });
    };

    const fetchAccount = async (lang: string, id: number) => {
        return fetch(getApiBaseUrl(lang).concat('/accounts', '/', id.toString()), {
            method: 'GET',
            headers: defaultHeader(),
        }).then((data: Response) => {
            return data;
        });
    };

    const fetchSoftware = async (lang: string) => {
        return fetch(getApiBaseUrl(lang).concat('/software'), {
            method: 'GET',
            headers: defaultHeader(),
        }).then((data: Response) => {
            return data;
        });
    };

    const fetchProvince = async (lang: string) => {
        return fetch(getApiBaseUrl(lang).concat('/provinces'), {
            method: 'GET',
            headers: defaultHeader(),
        }).then((data: Response) => {
            return data;
        });
    };

    const fetchRoles = async (lang: string) => {
        return fetch(getApiBaseUrl(lang).concat('/roles'), {
            method: 'GET',
            headers: defaultHeader(),
        }).then((data: Response) => {
            return data;
        });
    };

    const fetchIssuers = async (lang: string, roleId: number, provinceId: number) => {
        return fetch(
            getApiBaseUrl(lang).concat(
                '/role/',
                roleId.toString(),
                '/issuers?provinceId=',
                provinceId.toString()
            ),
            {
                method: 'GET',
                headers: defaultHeader(),
            }
        ).then((data: Response) => {
            return data;
        });
    };

    const fetchRegulations = async (lang: string, provinceId: number) => {
        return fetch(
            getApiBaseUrl(lang).concat('/regulations?provinceId=', provinceId.toString()),
            {
                method: 'GET',
                headers: defaultHeader(),
            }
        ).then((data: Response) => {
            return data;
        });
    };

    const createOrganization = async (lang: string, id: string, details: IOrganizationModel) => {
        return fetch(getApiBaseUrl(lang).concat('/accounts/', id.toString(), '/organizations'), {
            method: 'POST',
            headers: defaultHeader(),
            body: JSON.stringify(details),
        }).then((data: Response) => {
            return data;
        });
    };

    const updateOrganization = async (
        lang: string,
        accountId: string,
        details: IOrganizationModel
    ) => {
        return fetch(
            getApiBaseUrl(lang).concat('/accounts/', accountId.toString(), '/organizations'),
            {
                method: 'PUT',
                headers: defaultHeader(),
                body: JSON.stringify(details),
            }
        ).then((data: Response) => {
            return data;
        });
    };

    const deleteOrganization = async (lang: string, accountId: string) => {
        return fetch(
            getApiBaseUrl(lang).concat('/accounts', accountId.toString(), '/organizations'),
            {
                method: 'DELETE',
                headers: defaultHeader(),
            }
        ).then((data: Response) => {
            return data;
        });
    };

    const createLocation = async (lang: string, id: number, location: ILocationModel) => {
        return fetch(getApiBaseUrl(lang).concat('/accounts/', id.toString(), '/locations'), {
            method: 'POST',
            headers: defaultHeader(),
            body: JSON.stringify(location),
        }).then((data: Response) => {
            return data;
        });
    };

    const updateLocations = async (lang: string, id: number, locations: ILocationModel[]) => {
        return fetch(getApiBaseUrl(lang).concat('/accounts/', id.toString(), '/locations'), {
            method: 'PUT',
            headers: defaultHeader(),
            body: JSON.stringify(locations),
        }).then((data: Response) => {
            return data;
        });
    };

    const deleteLocations = async (lang: string, accountId: number) => {
        return fetch(getApiBaseUrl(lang).concat('/accounts', accountId.toString(), '/locations'), {
            method: 'DELETE',
            headers: defaultHeader(),
        }).then((data: Response) => {
            return data;
        });
    };

    const deleteALocation = async (lang: string, accountId: number, locationId: number) => {
        return fetch(
            getApiBaseUrl(lang).concat(
                '/accounts',
                accountId.toString(),
                '/locations/',
                locationId.toString()
            ),
            {
                method: 'DELETE',
                headers: defaultHeader(),
            }
        ).then((data: Response) => {
            return data;
        });
    };

    const updateProviders = async (lang: string, accountId: string, details: IProviderModel[]) => {
        return fetch(getApiBaseUrl(lang).concat('/accounts/', accountId.toString(), '/providers'), {
            method: 'PUT',
            headers: defaultHeader(),
            body: JSON.stringify(details),
        }).then((data: Response) => {
            return data;
        });
    };

    const updateUser = async (lang: string, accountId: string, details: IUserModel) => {
        return fetch(getApiBaseUrl(lang).concat('/accounts/', accountId.toString(), '/users'), {
            method: 'PUT',
            headers: defaultHeader(),
            body: JSON.stringify(details),
        }).then((data: Response) => {
            return data;
        });
    };

    const resetAccount = async (lang: string, accountId: string) => {
        return fetch(getApiBaseUrl(lang).concat('/accounts/', accountId.toString(), '/reset'), {
            method: 'POST',
            headers: defaultHeader(true),
        }).then((data: Response) => {
            return data;
        });
    };

    const createEligibility = async (lang: string, eligibility: IEligibilityModel) => {
        return fetch(getApiBaseUrl(lang).concat('/eligibility'), {
            method: 'POST',
            headers: defaultHeader(),
            body: JSON.stringify(eligibility),
        }).then((data: Response) => {
            return data;
        });
    };

    return {
        fetchInsurer,
        fetchSoftware,
        fetchProvince,
        fetchRoles,
        fetchIssuers,
        fetchRegulations,
        createAccount,
        sendEmail,
        fetchAccount,
        updateAccount,
        createOrganization,
        updateOrganization,
        deleteOrganization,
        createLocation,
        updateLocations,
        deleteLocations,
        deleteALocation,
        updateProviders,
        updateUser,
        resetAccount,
        createEligibility,
    };
}
